<script setup lang="ts">
import { useRouter } from 'vue-router';
import { onMounted, computed } from 'vue';
import { useAuth } from '@app/composables/useAuth';
import { useHydrated } from '@app/composables/useHydrated';
import { useHead } from '@unhead/vue';
import MegaHero from '@app/presentation/components/landing/MegaHero.vue';
import MegaConcerns from '@app/presentation/components/landing/MegaConcerns.vue';
import MegaHow from '@app/presentation/components/landing/MegaHow.vue';
import MegaChat from '@app/presentation/components/landing/MegaChat.vue';
import MegaSpreads from '@app/presentation/components/landing/MegaSpreads.vue';
import MegaProof from '@app/presentation/components/landing/MegaProof.vue';
import MegaHonest from '@app/presentation/components/landing/MegaHonest.vue';
import MegaRitual from '@app/presentation/components/landing/MegaRitual.vue';
import MegaSeo from '@app/presentation/components/landing/MegaSeo.vue';
import MegaFinalCTA from '@app/presentation/components/landing/MegaFinalCTA.vue';

const { isAuthenticated } = useAuth();
const isHydrated = useHydrated();

// Hydration-safe auth: the pre-rendered landing is always the guest variant, so
// the client's first render must match. `authed` only reads auth state after
// hydration, so it never diverges from the SSG output during hydration.
// (Authed users are redirected to /spread by the onMounted below anyway.)
const authed = computed(() => isHydrated.value && isAuthenticated.value);

const router = useRouter();

onMounted(() => {
  // Authed users never see the landing — bounce them to the app. Guard with the
  // hydration-safe `authed` so this only fires once auth state has settled.
  if (authed.value || isAuthenticated.value) {
    router.replace('/spread');
  }
});

useHead({
  title: 'Таро онлайн — тёплый расклад с котом | Таро Мяу',
  meta: [
    { name: 'description', content: 'Тёплый расклад таро под твой вопрос, не один шаблон на всех: 16 видов раскладов онлайн, чат с котом-тарологом, карта дня бесплатно. Спроси прямо сейчас.' },
    { name: 'keywords', content: 'таро онлайн, гадание таро, расклад таро, таро мяу, таро бесплатно, telegram таро бот' },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: 'Таро Мяу — расклады Таро' },
    { property: 'og:description', content: 'Расклады на картах Таро с личным разбором. 16 видов раскладов, чат с тарологом, обучение картам.' },
    { property: 'og:image', content: 'https://tarotmeow.ru/og-image.jpg' },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:locale', content: 'ru_RU' },
    { property: 'og:site_name', content: 'Таро Мяу' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: 'Таро Мяу — расклады Таро' },
    { name: 'twitter:description', content: 'Расклады на картах Таро с личным разбором. 16 видов раскладов, чат с тарологом.' },
    { name: 'twitter:image', content: 'https://tarotmeow.ru/og-image.jpg' },
  ],
  link: [
    { rel: 'canonical', href: 'https://tarotmeow.ru/' },
    { rel: 'alternate', hreflang: 'ru', href: 'https://tarotmeow.ru/' },
    { rel: 'alternate', hreflang: 'x-default', href: 'https://tarotmeow.ru/' },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Таро Мяу',
        alternateName: 'Tarot Meow',
        url: 'https://tarotmeow.ru',
        description: 'Расклады на картах Таро с персональной интерпретацией. 16 видов раскладов, чат с тарологом, обучение картам.',
        applicationCategory: 'LifestyleApplication',
        operatingSystem: 'Web, Telegram (iOS/Android)',
        inLanguage: 'ru',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'RUB' },
      }),
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Таро Мяу',
        alternateName: 'Tarot Meow',
        url: 'https://tarotmeow.ru',
        logo: 'https://tarotmeow.ru/android-chrome-512x512.png',
        description: 'Расклады на картах Таро с персональной интерпретацией',
        // Real external profiles only (the Telegram bot is the brand's sole public
        // off-site presence today). TODO(geo): add more once VK/channel/etc. exist.
        sameAs: ['https://t.me/TarotMeowBot'],
      }),
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Главная', item: 'https://tarotmeow.ru/' },
        ],
      }),
    },
  ],
});
</script>

<template>
  <div>
    <MegaHero />
    <MegaConcerns />
    <MegaHow />
    <MegaChat />
    <MegaSpreads />
    <MegaProof />
    <MegaHonest />
    <MegaRitual />
    <MegaFinalCTA />
    <MegaSeo />
  </div>
</template>
